import React from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { UserRole } from '../../models/user.model';

interface RoleBasedAccessProps {
  allowedRoles: UserRole[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Component that conditionally renders children based on user roles
 * 
 * @example
 * <RoleBasedAccess allowedRoles={[UserRole.Admin, UserRole.Manager]}>
 *   <RestrictedComponent />
 * </RoleBasedAccess>
 */
export const RoleBasedAccess: React.FC<RoleBasedAccessProps> = ({ 
  allowedRoles, 
  children, 
  fallback = null 
}) => {
  const { userRole } = useAuth();

  if (!userRole) {
    return fallback ? <>{fallback}</> : null;
  }

  const hasAccess = allowedRoles.includes(userRole);

  if (!hasAccess) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
};

/**
 * Component that only renders for Admin users
 */
export const AdminOnly: React.FC<{children: React.ReactNode, fallback?: React.ReactNode}> = ({ 
  children, 
  fallback 
}) => {
  return (
    <RoleBasedAccess allowedRoles={[UserRole.Admin]} fallback={fallback}>
      {children}
    </RoleBasedAccess>
  );
};

/**
 * Component that renders for Admin and Manager users
 */
export const ManagerOrAdmin: React.FC<{children: React.ReactNode, fallback?: React.ReactNode}> = ({ 
  children, 
  fallback 
}) => {
  return (
    <RoleBasedAccess allowedRoles={[UserRole.Admin, UserRole.Manager]} fallback={fallback}>
      {children}
    </RoleBasedAccess>
  );
}; 